"use client";

import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  fileResourceMark,
  ResourceMarkView,
  type ResourceMarkSource,
} from "./icons/file-type-icons";
import { linkEntity } from "./link-entity";

/*
 * One card for a thing someone made or shared: a Twin document or sheet, an
 * uploaded or attached file, a linked resource. A 40px tile with the thing's
 * picture (an image's own thumbnail, its product's mark or its format's
 * mark), then its name and one quiet line of facts. The channel feed, the
 * session transcript and the composer's attachments all draw it; each caller
 * keeps its own element (link, button or plain box) around the content. File
 * grids (a channel's Files, Knowledge folders) use the same tile at 64px.
 */

export type ResourceVisual =
  | ResourceMarkSource
  | { thumbnail: string; fallback: ResourceMarkSource };

const THUMBNAIL_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
]);

/** A file's picture: its thumbnail when a browser can draw one, else its mark. */
export function fileResourceVisual(
  file: { fileName: string; contentType?: string | null },
  thumbnailSrc?: string,
): ResourceVisual {
  const mark = fileResourceMark(file);
  const type = (file.contentType ?? "").split(";")[0]!.trim().toLowerCase();
  return thumbnailSrc && THUMBNAIL_TYPES.has(type)
    ? { thumbnail: thumbnailSrc, fallback: mark }
    : mark;
}

/** A link's picture: the product's mark when the address names one. */
export function linkResourceVisual(href: string | undefined): ResourceVisual {
  const icon = href ? linkEntity(href)?.icon : null;
  return icon ? { brand: icon } : { mark: "link" };
}

/*
 * An artifact's picture: a Twin document's or sheet's mark; for a link, its
 * product's mark, else the type its title names ("board pack.pdf"), else the
 * link mark.
 */
export function artifactResourceVisual(artifact: {
  kind?: string | null;
  href?: string | null;
  title?: string;
}): ResourceVisual {
  if (artifact.kind === "markdown") return { mark: "document" };
  if (artifact.kind === "csv") return { mark: "sheet" };
  if (artifact.kind === "html") return { mark: "slides" };
  const link = linkResourceVisual(artifact.href ?? undefined);
  if ("brand" in link || !artifact.title) return link;
  const byTitle = fileResourceMark({ fileName: artifact.title });
  return "mark" in byTitle && byTitle.mark === "file" ? link : byTitle;
}

/** The same picture at list size (16px by default): a mark, never a thumbnail. */
export function ResourceIcon({
  visual,
  className,
  title,
}: {
  visual: ResourceVisual;
  className?: string;
  title?: string;
}) {
  return (
    <ResourceMarkView
      source={"thumbnail" in visual ? visual.fallback : visual}
      className={cn("size-4", className)}
      title={title}
    />
  );
}

const TILE_SIZES = {
  card: { tile: "size-7 rounded-[6px]", mark: "size-4" },
  grid: { tile: "size-16 rounded-[11px]", mark: "size-9" },
};

/** The picture slot: a soft tile holding a thumbnail, a mark or any glyph. */
export function ResourceTileFrame({
  size = "card",
  children,
}: {
  size?: keyof typeof TILE_SIZES;
  children: ReactNode;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden bg-foreground/[0.05]",
        TILE_SIZES[size].tile,
      )}
    >
      {children}
    </span>
  );
}

export function ResourceTile({
  visual,
  size = "card",
}: {
  visual: ResourceVisual;
  size?: keyof typeof TILE_SIZES;
}) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const shown =
    "thumbnail" in visual && thumbnailFailed ? visual.fallback : visual;
  return (
    <ResourceTileFrame size={size}>
      {"thumbnail" in shown ? (
        <img
          src={shown.thumbnail}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setThumbnailFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        <ResourceMarkView source={shown} className={TILE_SIZES[size].mark} />
      )}
    </ResourceTileFrame>
  );
}

/** The card's frame. Add `resourceCardInteractiveClass` when it opens something. */
export const resourceCardClass =
  "flex min-w-0 items-center gap-2.5 rounded-[10px] border border-border/70 bg-surface py-1.5 pl-1.5 pr-3 text-left";

/** A card on its own line (feed, transcript) spans it, so cards line up. */
export const resourceCardLineWidthClass = "w-full";

export const resourceCardInteractiveClass =
  "transition-[border-color,box-shadow] hover:border-border hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * A card's contents on one line: a small tile, then the name and its facts side by side. Short of
 * room, the facts give way first and the name truncates; neither wraps.
 */
export function ResourceCardContent({
  visual,
  title,
  meta,
  trailing,
}: {
  visual: ResourceVisual;
  title: string;
  meta?: string;
  /** A status or action after the text: an operation badge, a remove button. */
  trailing?: ReactNode;
}) {
  return (
    <>
      <ResourceTile visual={visual} />
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span
          title={title}
          className="min-w-0 truncate text-sm font-medium leading-5 text-foreground"
        >
          {title}
        </span>
        {meta ? (
          <span className="min-w-0 shrink-[4] truncate text-xs leading-4 tabular-nums text-muted-foreground">
            {meta}
          </span>
        ) : null}
      </span>
      {trailing}
    </>
  );
}
